import { UzButton, UzInput } from '@utazzo/ui';
import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateFixedDeparture } from '../use-fixed-departures';

import { useListReturn } from '@/hooks/use-list-return';

/**
 * Step 0 of the builder: name the template. Everything else (cities, days,
 * option pools) is filled in on the detail page once the row exists, so this
 * stays a single field - the same "create a shell, then build it" flow the
 * itinerary builder uses.
 */
export const NewFixedDeparturePage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn('/fixed-departures');
  const create = useCreateFixedDeparture();
  const [title, setTitle] = useState('');
  const trimmed = title.trim();

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!trimmed) return;
    const fd = await create.mutateAsync({ title: trimmed });
    navigate(`/fixed-departures/${fd.id}`, { replace: true });
  };

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to fixed departures"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Fixed Departures</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            New fixed departure
          </span>
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl min-w-0 px-6 py-10 lg:px-8">
        <h1 className="text-lg font-semibold tracking-tight text-zinc-900">
          Name this fixed departure
        </h1>
        {/* <p className="mt-1 text-sm text-zinc-500">
          A fixed departure is a reusable itinerary template. Next you will add the cities and
          nights, which generates the day-by-day plan for you to fill in.
        </p> */}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <UzInput
            label="Package title"
            required
            autoFocus
            placeholder="e.g. Kashmir Fixed Departure - 5N/6D"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="flex items-center gap-2">
            <UzButton type="submit" disabled={!trimmed} loading={create.isPending}>
              Create and continue
            </UzButton>
            <UzButton type="button" variant="ghost" onClick={backToList}>
              Cancel
            </UzButton>
          </div>
        </form>
      </div>
    </div>
  );
};
